import React from 'react'
import { CloseCircle, Edit2 } from 'iconsax-react'
import { useAppDispatch } from '../../../../hooks/redux'
import { useDrag } from 'react-dnd'
import { IconButton, useTheme } from '@mui/material'

import EditFieldModal from '../FieldModal/EditFieldModal'
import FieldIcon from '../FieldIcon'

import { updateCustomFields } from '../../../../slices/customFields'
import { AddSuccessAlert } from '../../../../slices/alert'
import { useAreYouSure } from '../../../../providers/AreYouSureProvider'

import useStyles from './styles'
import Tooltip from '@SonarCX/components/atoms/Tooltip'

export const ITEM_TYPE = 'CUSTOM_FIELD'

type CustomFieldProps = {
  field: APICustomField
  hideActions?: boolean
}

const CustomField: React.FC<CustomFieldProps> = ({
  field,
  hideActions = false
}) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const { setAction, setReason, setSubtext, setV2 } = useAreYouSure()

  const [editModalOpen, setEditModalOpen] = React.useState(false)

  const [{ opacity }, dragRef, previewRef] = useDrag({
    item: { type: ITEM_TYPE, id: field.id },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  })

  const classes = useStyles()

  const startEdit = React.useCallback(() => {
    setEditModalOpen(true)
  }, [])

  const endEdit = React.useCallback(() => {
    setEditModalOpen(false)
  }, [])

  const checkDelete = React.useCallback(() => {
    setReason(`Delete ${field.title}`)
    setSubtext('Are you sure you want to delete this field?')
    setV2(true)
    setAction(() => {
      const { id, title, object_type, description, field_type, config } = field

      const update: CustomFieldUpdate = {
        id,
        title,
        object_type,
        description,
        field_type,
        config,
        archived_at: new Date().toISOString()
      }
      dispatch(
        updateCustomFields([update], () => {
          dispatch(AddSuccessAlert('Changes saved', 500))
        })
      )
    })
  }, [setReason, setSubtext, setV2, setAction, field, dispatch])

  return (
    <>
      <div
        ref={previewRef}
        className={classes.root}
        style={{ opacity, cursor: 'pointer' }}
      >
        <div ref={dragRef} className={classes.title}>
          <FieldIcon type={field.field_type} className={classes.icon} />

          {field.title}
        </div>

        {!hideActions && (
          <div className={classes.controls}>
            <Tooltip title='edit' arrow>
              <IconButton size='small' onClick={startEdit}>
                <Edit2 size={theme.size.sm} />
              </IconButton>
            </Tooltip>

            <Tooltip title='remove' arrow>
              <IconButton size='small' onClick={checkDelete}>
                <CloseCircle size={theme.size.sm} />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      {editModalOpen && <EditFieldModal field={field} onClose={endEdit} />}
    </>
  )
}

export default CustomField
